import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AddIcon from '@mui/icons-material/Add';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';

import { useSelectMenuProps } from '../../utilities/ModalStackContext';
import CanvasPage from './CanvasPage';
import { KINDS, newId, normalizeFieldPage } from './constants';

export { KINDS, newId, b64ToBytes, renderPdf, normalizeFieldPage, toFieldMap, kindMeta } from './constants';

// Signature-placement editor. Staff drag signature/initial/date/name boxes onto
// a rendered PDF and assign each to a role. Extracted from the Signature
// Templates page so the bespoke-document send flow places boxes the same way —
// there is exactly one implementation of the coordinate convention (a block's
// BOTTOM edge is the signing line, matching esign/stamp.js and the agora
// signing page), and it must stay that way.
//
// Controlled: the caller owns `fields` and receives every edit through
// `onFieldsChange`. Selection, clipboard and keyboard handling live in here.
//
// Props
//   pages          [{ page, widthPts, heightPts }] from renderPdf()
//   fields         placed blocks
//   onFieldsChange (nextFields) => void
//   roles          [{ key, label }] — who can be assigned a block
//   state          'idle' | 'loading' | 'ready' | 'error'
//   title          node rendered at the left of the toolbar
//   toolbarExtras  node rendered at the right of the toolbar (Save, etc.)
//   idleContent / errorContent   what to show in place of the document
//   showInspector  render the right-hand properties panel (default true)
export default function SignaturePlacement({
  pages = [],
  fields = [],
  onFieldsChange,
  roles = [],
  state = 'ready',
  title = null,
  toolbarExtras = null,
  idleContent = null,
  errorContent = null,
  showInspector = true,
  minRenderWidth = 360,
  maxRenderWidth = 900,
  // The render scale is owned here (it follows the container width), but
  // normalizeFieldPage needs it at save time. Report it so callers can
  // normalize without duplicating the resize logic.
  onScaleChange,
  // Selection lives here too; callers that show a count in their own toolbar
  // get it through this.
  onSelectionChange,
}) {
  const [selectedIds, setSelectedIds] = React.useState([]);
  const [addRole, setAddRole] = React.useState('');
  const [addKind, setAddKind] = React.useState('signature');
  const [renderWidth, setRenderWidth] = React.useState(720);
  const [clipboard, setClipboard] = React.useState(null); // array of field templates
  const dragSnap = React.useRef(null);
  const centerRef = React.useRef(null);
  const menuProps = useSelectMenuProps();

  const scale = pages.length ? renderWidth / pages[0].widthPts : 1;
  React.useEffect(() => { if (onScaleChange) onScaleChange(scale); }, [scale, onScaleChange]);
  React.useEffect(() => { if (onSelectionChange) onSelectionChange(selectedIds); }, [selectedIds, onSelectionChange]);
  const selectedSet = React.useMemo(() => new Set(selectedIds), [selectedIds]);
  const selectedFields = fields.filter((f) => selectedSet.has(f.id));
  const selected = selectedFields.length === 1 ? selectedFields[0] : null;

  const roleLabel = React.useCallback(
    (r) => (roles.find((x) => x.key === r) || { label: r }).label,
    [roles],
  );

  // Keep the "add for" role pointed at something that exists — the caller's
  // role list changes as signers are added or removed.
  React.useEffect(() => {
    if (!roles.length) return;
    if (!roles.some((r) => r.key === addRole)) setAddRole(roles[0].key);
  }, [roles, addRole]);

  // Drop any selection referring to fields the caller has replaced.
  React.useEffect(() => {
    setSelectedIds((prev) => prev.filter((id) => fields.some((f) => f.id === id)));
  }, [fields]);

  React.useEffect(() => {
    const el = centerRef.current; if (!el) return;
    const update = () => setRenderWidth(Math.max(minRenderWidth, Math.min(maxRenderWidth, el.clientWidth - 64)));
    update();
    const ro = new ResizeObserver(update); ro.observe(el);
    return () => ro.disconnect();
  }, [minRenderWidth, maxRenderWidth]);

  const setFields = React.useCallback((updater) => {
    onFieldsChange(typeof updater === 'function' ? updater(fields) : updater);
  }, [fields, onFieldsChange]);

  // ── selection ──────────────────────────────────────────────────────────────
  const selectField = (id, additive) => {
    let next;
    if (additive) next = selectedIds.includes(id) ? selectedIds.filter((x) => x !== id) : [...selectedIds, id];
    else next = selectedIds.includes(id) ? selectedIds : [id]; // keep group if already selected (so drag moves all)
    setSelectedIds(next);
    return next;
  };
  const clearSelection = () => setSelectedIds([]);
  const marqueeSelect = (pageIndex, r, additive) => {
    const hit = fields.filter((f) => f.page === pageIndex && f.x < r.maxX && f.x + f.w > r.minX && f.y < r.maxY && f.y + f.h > r.minY).map((f) => f.id);
    setSelectedIds(additive ? Array.from(new Set([...selectedIds, ...hit])) : hit);
  };

  // ── group drag ───────────────────────────────────────────────────────────
  const beginDrag = (ids) => { dragSnap.current = fields.filter((f) => ids.includes(f.id)).map((f) => ({ id: f.id, x: f.x, y: f.y })); };
  const moveBy = (dxPts, dyPts) => {
    const snap = dragSnap.current; if (!snap) return;
    setFields((p) => p.map((f) => {
      const s = snap.find((x) => x.id === f.id);
      return s ? { ...f, x: Math.round(s.x + dxPts), y: Math.round(s.y - dyPts) } : f;
    }));
  };

  // ── add / edit / remove ─────────────────────────────────────────────────
  const addField = (pageIndex, xPts, yPts) => {
    const role = addRole || (roles[0] && roles[0].key);
    if (!role) return;
    const f = { id: newId(), page: pageIndex, x: Math.round(xPts), y: Math.round(yPts), w: 170, h: 30, role, kind: addKind, required: true, ...(addKind === 'date' ? { dateFormat: 'MM/DD/YYYY' } : {}) };
    setFields((p) => [...p, f]); setSelectedIds([f.id]);
  };
  const addDefault = () => { if (pages.length) addField(0, 80, pages[0].heightPts - 130); };
  const change = (id, patch) => setFields((p) => p.map((f) => (f.id === id ? { ...f, ...patch } : f)));
  const changeMany = (ids, patch) => { const set = new Set(ids); setFields((p) => p.map((f) => (set.has(f.id) ? { ...f, ...patch } : f))); };
  const removeMany = (ids) => { const set = new Set(ids); setFields((p) => p.filter((f) => !set.has(f.id))); setSelectedIds([]); };

  // ── copy / paste / duplicate (one or many) ───────────────────────────────
  const pasteFrom = (list) => {
    if (!list || !list.length) return;
    const created = list.map((f) => ({ ...f, id: newId(), x: Math.round(f.x + 12), y: Math.round(f.y - 12) }));
    setFields((p) => [...p, ...created]); setSelectedIds(created.map((f) => f.id));
  };

  // Keyboard: ⌘/Ctrl+C copy, +V paste, +D duplicate, +A select all, Delete remove.
  React.useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || e.target.isContentEditable) return;
      const mod = e.metaKey || e.ctrlKey;
      const k = e.key.toLowerCase();
      if (mod && k === 'a' && fields.length) { e.preventDefault(); setSelectedIds(fields.map((f) => f.id)); }
      else if (mod && k === 'c' && selectedFields.length) { e.preventDefault(); setClipboard(selectedFields.map((f) => ({ ...f }))); }
      else if (mod && k === 'v' && clipboard) { e.preventDefault(); pasteFrom(clipboard); }
      else if (mod && k === 'd' && selectedFields.length) { e.preventDefault(); pasteFrom(selectedFields); }
      else if ((e.key === 'Delete' || e.key === 'Backspace') && selectedFields.length) { e.preventDefault(); removeMany(selectedIds); }
      else if (e.key === 'Escape') { clearSelection(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedIds, clipboard, fields]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box sx={{ display: 'flex', flex: 1, minHeight: 0, minWidth: 0 }}>
      {/* CENTER */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <Box sx={{ px: { xs: 2, md: 3 }, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 1.5, rowGap: 1.5, bgcolor: '#fff', minHeight: 64 }}>
          {state === 'idle' && !title ? (
            <Typography variant="body2" color="text.secondary">Nothing to place yet.</Typography>
          ) : (
            <>
              <Box sx={{ minWidth: 140, mr: 'auto' }}>{title}</Box>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Add for</InputLabel>
                <Select label="Add for" value={addRole} MenuProps={menuProps} onChange={(e) => setAddRole(e.target.value)}>
                  {roles.map((r) => <MenuItem key={r.key} value={r.key}>{r.label}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 130 }}>
                <InputLabel>Field</InputLabel>
                <Select label="Field" value={addKind} MenuProps={menuProps} onChange={(e) => setAddKind(e.target.value)}>
                  {KINDS.map((k) => <MenuItem key={k.key} value={k.key}>{k.label}</MenuItem>)}
                </Select>
              </FormControl>
              <Button variant="outlined" startIcon={<AddIcon />} onClick={addDefault} disabled={!pages.length}>Add field</Button>
              {toolbarExtras}
            </>
          )}
        </Box>

        <Box ref={centerRef} sx={{ flex: 1, overflowY: 'auto', bgcolor: '#f2f3f5', p: 4 }}>
          {state === 'idle' && (idleContent || (
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'text.secondary', gap: 1 }}>
              <DescriptionOutlinedIcon sx={{ fontSize: 52, color: 'text.disabled' }} />
              <Typography variant="body1">No document loaded</Typography>
            </Box>
          ))}
          {state === 'loading' && <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>}
          {state === 'error' && (errorContent || (
            <Typography sx={{ textAlign: 'center', mt: 8, color: 'text.secondary' }}>Couldn’t load this document.</Typography>
          ))}
          {state === 'ready' && pages.map((pi, i) => (
            <CanvasPage key={i} page={pi.page} pageIndex={i} widthPts={pi.widthPts} heightPts={pi.heightPts} scale={scale}
              fields={fields} selectedSet={selectedSet} roleLabel={roleLabel} onSelect={selectField} onBeginDrag={beginDrag}
              onMoveBy={moveBy} onResize={change} onAddAt={addField} onMarquee={marqueeSelect}
              onClearSelection={(additive) => { if (!additive) clearSelection(); }} />
          ))}
        </Box>
      </Box>

      {/* RIGHT PROPERTIES */}
      {showInspector && (
        <Box sx={{ width: 260, flexShrink: 0, borderLeft: '1px solid', borderColor: 'divider', bgcolor: '#fbfbfc', overflowY: 'auto', display: { xs: 'none', md: 'block' } }}>
          {selectedFields.length === 0 && (
            <Box sx={{ p: 2.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Field types</Typography>
              {KINDS.map((k) => { const I = k.icon; return (
                <Box key={k.key} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.75 }}>
                  <Box sx={{ width: 22, height: 22, borderRadius: 1, bgcolor: `${k.color}1a`, color: k.color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><I sx={{ fontSize: 15 }} /></Box>
                  <Typography variant="body2">{k.label}</Typography>
                </Box>
              ); })}
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>Select a field to edit it — Shift/⌘-click or drag a box on the page to select several.</Typography>
              <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1.5 }}>
                ⌘/Ctrl + C / V copy &amp; paste · +D duplicate · +A select all · Delete removes. Works on one or many.
              </Typography>
            </Box>
          )}

          {selectedFields.length > 1 && (
            <Box sx={{ p: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{selectedFields.length} fields selected</Typography>
                <Box>
                  <Tooltip title="Duplicate all (⌘/Ctrl + D)"><IconButton size="small" onClick={() => pasteFrom(selectedFields)}><ContentCopyIcon fontSize="small" /></IconButton></Tooltip>
                  <Tooltip title="Delete all (Del)"><IconButton size="small" color="error" onClick={() => removeMany(selectedIds)}><DeleteOutlineIcon fontSize="small" /></IconButton></Tooltip>
                </Box>
              </Box>
              <Typography variant="overline" color="text.disabled">Set for all selected</Typography>
              <FormControl size="small" fullWidth sx={{ mt: 0.5, mb: 1.5 }}>
                <InputLabel>Role</InputLabel>
                <Select label="Role" value="" displayEmpty MenuProps={menuProps} onChange={(e) => changeMany(selectedIds, { role: e.target.value })}>
                  <MenuItem value="" disabled>Change role…</MenuItem>
                  {roles.map((r) => <MenuItem key={r.key} value={r.key}>{r.label}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl size="small" fullWidth sx={{ mb: 1.5 }}>
                <InputLabel>Type</InputLabel>
                <Select label="Type" value="" displayEmpty MenuProps={menuProps} onChange={(e) => changeMany(selectedIds, { kind: e.target.value, ...(e.target.value === 'date' ? { dateFormat: 'MM/DD/YYYY' } : {}) })}>
                  <MenuItem value="" disabled>Change type…</MenuItem>
                  {KINDS.map((k) => <MenuItem key={k.key} value={k.key}>{k.label}</MenuItem>)}
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary">Drag any selected field to move them together. Copy/paste (⌘/Ctrl+C/V) works on the whole group.</Typography>
            </Box>
          )}

          {selected && (
            <Box sx={{ p: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Field</Typography>
                <Box>
                  <Tooltip title="Duplicate (⌘/Ctrl + D)"><IconButton size="small" onClick={() => pasteFrom([selected])}><ContentCopyIcon fontSize="small" /></IconButton></Tooltip>
                  <Tooltip title="Delete (Del)"><IconButton size="small" color="error" onClick={() => removeMany([selected.id])}><DeleteOutlineIcon fontSize="small" /></IconButton></Tooltip>
                </Box>
              </Box>
              <FormControl size="small" fullWidth sx={{ mb: 1.5 }}>
                <InputLabel>Role</InputLabel>
                <Select label="Role" value={selected.role} MenuProps={menuProps} onChange={(e) => change(selected.id, { role: e.target.value })}>
                  {roles.map((r) => <MenuItem key={r.key} value={r.key}>{r.label}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl size="small" fullWidth sx={{ mb: 1.5 }}>
                <InputLabel>Type</InputLabel>
                <Select label="Type" value={selected.kind} MenuProps={menuProps} onChange={(e) => change(selected.id, { kind: e.target.value, ...(e.target.value === 'date' && !selected.dateFormat ? { dateFormat: 'MM/DD/YYYY' } : {}) })}>
                  {KINDS.map((k) => <MenuItem key={k.key} value={k.key}>{k.label}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControlLabel sx={{ mb: 1 }} control={<Switch checked={selected.required !== false} onChange={(e) => change(selected.id, { required: e.target.checked })} />} label="Required" />
              <Typography variant="overline" color="text.disabled">Position (pts)</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mt: 0.5 }}>
                {['x', 'y', 'w', 'h'].map((k) => (
                  <TextField key={k} size="small" label={k.toUpperCase()} type="number" value={Math.round(selected[k])}
                    onChange={(e) => change(selected.id, { [k]: Number(e.target.value) || 0 })} />
                ))}
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>Page {selected.page + 1}. Drag on the document or fine-tune here.</Typography>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}

// Convenience for callers that need the on-save normalization: re-map every
// field onto the page it visually sits on. See normalizeFieldPage.
export const normalizeAll = (fields, pages, scale) => fields.map((f) => normalizeFieldPage(f, pages, scale));
